import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { filter, first } from 'rxjs/operators';
import { BusinessTransactionOverrideResponse } from '../../../shared/models/business-transaction/business-transaction-override-response.model';
import { BusinessTransactionWorkQueueDetailModel } from '../../../shared/models/business-transaction/business-transaction-work-queue-detail-model';
import { BusinessTransactionService } from '../../../shared/services/business-transaction/business-transaction.service';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { SystemNotificationService } from '../../../shared/services/realtime-messaging/system-notification.service';
import { AssignmentBase } from '../assignment-base.component';
import { OverrideRequestData, OverrideRequestWindow } from '../modals/override-request-modal.component';
import { NotifyRequest } from '../transaction-queue.component';

interface EscalationQueueTransaction {
    checked: boolean;
    previousAction: string;
}

@Component({
    selector: 'escalation-queue-component',
    templateUrl: './escalation-queue.html',
})
export class EscalationQueueComponent extends AssignmentBase {
    @Input() businessTransactionDetails: (BusinessTransactionWorkQueueDetailModel & EscalationQueueTransaction)[];
    @Input() userId: any;
    @Input() hideEscalateOption: boolean;
    @Output() onRedirect = new EventEmitter<BusinessTransactionWorkQueueDetailModel>();
    @Output() onRespond = new EventEmitter<any>();
    @Output() onNotify = new EventEmitter<NotifyRequest>();

    @ViewChild('table', { static: true }) table: Table;

    showGroupedCheckbox = true;
    showButtonLoading = false;
    cols: any[];
    rowGroupMetadata: any;

    DROPDOWN_ALLOW = 1;
    DROPDOWN_DENY = 2;
    DROPDOWN_ESCALATE = 3;

    DROPDOWN_UNASSIGNED = 1;
    DROPDOWN_REQUESTING_CSR = 2;

    dropdownActionOptions = [{ label: '- Select -' }, { label: 'Allow', value: this.DROPDOWN_ALLOW }, { label: 'Deny', value: this.DROPDOWN_DENY }, { label: 'Escalate', value: this.DROPDOWN_ESCALATE }];
    dropdownAssignmentOptions = [{ label: '- Select -' }, { label: 'Requesting CSR', value: this.DROPDOWN_REQUESTING_CSR }, { label: 'Unassigned', value: this.DROPDOWN_UNASSIGNED }];
    selectedActionOption: any;
    selectedAssignmentOption: any;

    constructor(
        public systemNotificationService: SystemNotificationService,
        public businessTransactionService: BusinessTransactionService,
        public modalService: MvdModalService
    ) {
        super(modalService, systemNotificationService, businessTransactionService);
    }

    ngOnInit() {

        // Exclude escalate option for the queue which does not have
        // escalate queue name
        if (this.hideEscalateOption) {
            this.dropdownActionOptions = this.dropdownActionOptions.filter(x => x.value !== this.DROPDOWN_ESCALATE);
        }
        this.cols = [
            { field: 'agentName', header: 'Agent' },
            { field: 'customerName', header: 'Customer Name' },
            { field: 'transactionType', header: 'Service' },
            { field: 'escalationType', header: 'Reason' },
            { field: 'overrideRequestedByName', header: 'Requested By' },
            { field: 'displayUpdatedDate', header: 'Date/Time' },
            { field: 'assignedTo', header: 'Assigned To' },
            { field: 'displayElapsedTime', header: 'Elapsed Time' },
            { field: 'transactionId', header: 'Service ID#' },
            { field: 'previousAction', header: 'Previous Action' },
        ];

        this.updateRowGroupMetaData();
    }

    onSort() {
        this.updateRowGroupMetaData();
    }

    redirectToMyTransaction(businessTransaction: BusinessTransactionWorkQueueDetailModel) {
        this.onRedirect.emit(businessTransaction);
    }

    updateRowGroupMetaData() {
        this.rowGroupMetadata = {};

        if (this.businessTransactionDetails) {

            for (let i = 0; i < this.businessTransactionDetails.length; i++) {

                let rowData = this.businessTransactionDetails[i];
                let transactionId = rowData.transactionId;

                if (i == 0) {

                    this.rowGroupMetadata[transactionId] = { index: 0, size: 1 };

                } else {

                    let previousRowData = this.businessTransactionDetails[i - 1];
                    let previousRowGroup = previousRowData.transactionId;

                    if (transactionId === previousRowGroup) {

                        this.rowGroupMetadata[transactionId].size++;
                    } else {

                        this.rowGroupMetadata[transactionId] = { index: i, size: 1 };
                    }
                }
            }
        }
    }

    isFirstRecordForTransaction(selectedBusinessTransaction) {
        for (let i = 0; i < this.businessTransactionDetails.length; i++) {
            const transaction = this.businessTransactionDetails[i];

            if (transaction.transactionId == selectedBusinessTransaction.transactionId) {
                if (transaction.businessTransactionOverrideId == selectedBusinessTransaction.businessTransactionOverrideId) {
                    return true;
                } else {
                    return false;
                }
            }
        }

        return false;
    }

    toggleAllForBusinessTransaction($event, transactionId) {
        for (let i = 0; i < this.businessTransactionDetails.length; i++) {
            const transaction = this.businessTransactionDetails[i];

            if (transaction.transactionId == transactionId) {
                transaction.checked = $event.target.checked;
            }
        }
    }

    private sendResponseMessages(action: number, transactions: any[]) {
        let actionString: string;
        switch (action) {
            case this.DROPDOWN_ALLOW:
                actionString = 'Approved';
                break;
            case this.DROPDOWN_DENY:
                actionString = 'Denied';
                break;
            case this.DROPDOWN_ESCALATE:
                actionString = 'Escalated';
                break;
        }

        this.onNotify.emit({
            actionString: actionString,
            transactions: transactions
        });
    }

    async onGoButtonClicked() {
        const self = this;
        try {
            this.showButtonLoading = true;
            const selectedTransactions = this.businessTransactionDetails.filter(x => x.checked);
            if (this.selectedActionOption == this.DROPDOWN_ALLOW || this.selectedActionOption == this.DROPDOWN_DENY) {
                const responses = selectedTransactions.map(x => new BusinessTransactionOverrideResponse(x.businessTransactionOverrideId, this.selectedActionOption == this.DROPDOWN_ALLOW));
                await this.businessTransactionService.respondToEscalations(responses).pipe(first()).toPromise();
                this.sendResponseMessages(this.selectedActionOption, selectedTransactions);

                // Mark selected transactions with the action that was selected
                const affectedTransactions = this.businessTransactionDetails.filter(x => selectedTransactions.filter(y => x.businessTransactionOverrideId == y.businessTransactionOverrideId).length > 0);
                const actionText = this.dropdownActionOptions.find(x => x['value'] == this.selectedActionOption);
                affectedTransactions.forEach(x => {
                    x.previousAction = actionText && actionText.label;
                });

                const businessTransactionIdsToRemove = new Array();

                // If all override requests for this transaction have been addressed, remove the business transaction from the UI
                for (let i = 0; i < selectedTransactions.length; i++) {
                    const selectedTransactionNumber = selectedTransactions[i].transactionId;

                    if (this.allAddressedForBusinessTransactionNumber(selectedTransactionNumber)) {
                        if (this.showAssignmentOptions() && this.selectedAssignmentOption == this.DROPDOWN_UNASSIGNED) {
                            await self.businessTransactionService.returnBusinessTransactionToQueue(selectedTransactionNumber).pipe(first()).toPromise();
                        }
                        businessTransactionIdsToRemove.push(selectedTransactionNumber);
                    }
                }

                if (this.anyTransactionsWillbeFullyAddressed()) {
                    this.onRespond.emit();
                }

                for (let i = 0; i < businessTransactionIdsToRemove.length; i++) {
                    const selectedTransactionNumber = businessTransactionIdsToRemove[i];
                    this.businessTransactionDetails = this.businessTransactionDetails.filter(x => x.transactionId != selectedTransactionNumber);
                }

                this.table.value = this.businessTransactionDetails;
            } else if (this.selectedActionOption == this.DROPDOWN_ESCALATE) {
                for (let i = 0; i < selectedTransactions.length; i++) {
                    await this.businessTransactionService.escalate(selectedTransactions[i].businessTransactionWorkQueueId).pipe(first()).toPromise();
                }
                this.businessTransactionDetails = this.businessTransactionDetails.filter(x => selectedTransactions.filter(y => x.businessTransactionOverrideId == y.businessTransactionOverrideId).length == 0);
                this.table.value = this.businessTransactionDetails;
            }
        } catch (err) {
            this.onRespond.emit(err);
        } finally {
            this.showButtonLoading = false;
        }
    }

    disableGoButton(): boolean {
        const selectedTransactions = this.businessTransactionDetails.filter(x => x.checked);
        return selectedTransactions.length == 0 || (!this.selectedActionOption && !this.showAssignmentOptions()) || (this.showAssignmentOptions() && !this.selectedAssignmentOption);
    }

    showAssignmentOptions() {
        return (this.selectedActionOption == this.DROPDOWN_ALLOW || this.selectedActionOption == this.DROPDOWN_DENY) && this.anyTransactionsWillbeFullyAddressed();
    }

    allCheckedForBusinessTransactionNumber(businessTransactionNumberId) {
        for (let i = 0; i < this.businessTransactionDetails.length; i++) {
            const transaction = this.businessTransactionDetails[i];

            if (transaction.transactionId == businessTransactionNumberId) {
                if (!transaction.checked) {
                    return false;
                }
            }
        }

        return true;
    }

    allAddressedForBusinessTransactionNumber(businessTransactionNumberId) {
        for (let i = 0; i < this.businessTransactionDetails.length; i++) {
            const transaction = this.businessTransactionDetails[i];

            if (transaction.transactionId == businessTransactionNumberId) {
                if (!transaction.previousAction) {
                    return false;
                }
            }
        }

        return true;
    }

    allMarkedForBusinessTransactionNumber(businessTransactionNumberId) {
        for (let i = 0; i < this.businessTransactionDetails.length; i++) {
            const transaction = this.businessTransactionDetails[i];

            if (transaction.transactionId == businessTransactionNumberId) {
                if (!(transaction.previousAction || transaction.checked)) {
                    return false;
                }
            }
        }

        return true;
    }

    allChecked() {
        if (!this.businessTransactionDetails.length) {
            return false;
        }

        for (let i = 0; i < this.businessTransactionDetails.length; i++) {
            const transaction = this.businessTransactionDetails[i];

            if (!transaction.checked) {
                return false;
            }
        }

        return true;
    }

    toggleAll() {
        if (!this.allChecked()) {
            for (let i = 0; i < this.businessTransactionDetails.length; i++) {
                this.businessTransactionDetails[i].checked = true;
            }
        } else {
            for (let i = 0; i < this.businessTransactionDetails.length; i++) {
                this.businessTransactionDetails[i].checked = false;
            }
        }
    }

    anyTransactionsWillbeFullyAddressed() {
        const requestsThatAreMarked = this.businessTransactionDetails.filter(x => x.checked || x.previousAction);
        const transactionNumbers = new Array();

        for (let i = 0; i < requestsThatAreMarked.length; i++) {
            if (!transactionNumbers.find(x => requestsThatAreMarked[i].transactionId == x)) {
                transactionNumbers.push(requestsThatAreMarked[i].transactionId);
            }
        }

        for (let i = 0; i < transactionNumbers.length; i++) {
            if (this.allMarkedForBusinessTransactionNumber(transactionNumbers[i])) {
                return true;
            }
        }

        return false;
    }

    public openModal = (transaction: BusinessTransactionWorkQueueDetailModel): void => {
        const queueData = { escalationWorkQueueId: transaction.workQueueId };
        const data = new OverrideRequestData(transaction, queueData);
        this.modalService.open(OverrideRequestWindow, data).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe();
    };
}
